// ============================================================
// HARMONIC CHARTER — Song Store (Zustand)
// ============================================================

import { create } from 'zustand';
import { v4 as uuid } from 'uuid';
import type {
  Song,
  Section,
  Bar,
  Key,
  TimeSignature,
  DisplayMode,
  EditorSelection,
  HChartFile,
  AnalysisSettings,
  Annotation,
  AnnotationStyle,
  Degree,
  ChordQuality,
  ChordExtension,
} from '../core/types';
import { DEFAULT_ANNOTATION_STYLE } from '../core/types';
import { parseBar } from '../core/parser';
import { transposeSong, setSectionKey } from '../core/transposer';
import { transposeKey } from '../core/theory';
import { isBorrowed, DEFAULT_ANALYSIS_SETTINGS } from '../core/analysis';

// ------------------------------------------------------------
// CONSTANTS
// ------------------------------------------------------------

const STORAGE_KEY = 'harmonic-charter:autosave';
const FILE_VERSION = '1.0';
const MAX_UNDO_STEPS = 50;

// ------------------------------------------------------------
// DEFAULT FACTORIES
// ------------------------------------------------------------

function makeDefaultBar(): Bar {
  return {
    id: uuid(),
    chords: [],
    timeSignature: null,
    repeatStart: false,
    repeatEnd: null,
    eighthNotes: false,
    hasCustomRhythm: false,
    note: null,
    lyrics: null,
  };
}

function makeDefaultSection(title = 'Verse'): Section {
  return {
    id: uuid(),
    title,
    key: null,
    barsPerRow: null,
    note: null,
    bars: [
      makeDefaultBar(),
      makeDefaultBar(),
      makeDefaultBar(),
      makeDefaultBar(),
    ],
  };
}

export function makeDefaultSong(): Song {
  return {
    id: uuid(),
    title: 'Untitled',
    defaultKey: { root: 'C', scale: 'major' },
    timeSignature: { numerator: 4, denominator: 4 },
    displayMode: 'nashville',
    tempo: null,
    feel: null,
    note: null,
    barsPerRow: 4,
    analysisSettings: { ...DEFAULT_ANALYSIS_SETTINGS },
    sections: [makeDefaultSection('Verse')],
    annotations: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

// ------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------

export function effectiveKey(song: Song, section: Section): Key {
  return section.key ?? song.defaultKey;
}

export function effectiveTimeSignature(song: Song, bar: Bar): TimeSignature {
  return bar.timeSignature ?? song.timeSignature;
}

function pushUndo(stack: Song[], song: Song): Song[] {
  return [...stack.slice(-(MAX_UNDO_STEPS - 1)), JSON.parse(JSON.stringify(song))];
}

// ------------------------------------------------------------
// STORE SHAPE
// ------------------------------------------------------------

export interface SongStore {
  // ── State ──────────────────────────────────────────────────
  song: Song;
  selection: EditorSelection | null;
  isDirty: boolean;
  undoStack: Song[];
  redoStack: Song[];

  // ── Selection ──────────────────────────────────────────────
  setSelection: (selection: EditorSelection | null) => void;

  // ── Song metadata ──────────────────────────────────────────
  setSongTitle: (title: string) => void;
  setSongKey: (key: Key) => void;
  setSongTimeSignature: (ts: TimeSignature) => void;
  setSongTempo: (tempo: string | null) => void;
  setSongFeel: (feel: string | null) => void;
  setSongNote: (note: string | null) => void;
  setDisplayMode: (mode: DisplayMode) => void;
  setAnalysisSettings: (settings: Partial<AnalysisSettings>) => void;
  setSongBarsPerRow: (n: number) => void;

  // ── Section operations ─────────────────────────────────────
  addSection: (title?: string) => void;
  deleteSection: (sectionId: string) => void;
  duplicateSection: (sectionId: string) => void;
  updateSectionTitle: (sectionId: string, title: string) => void;
  setSectionNote: (sectionId: string, note: string | null) => void;
  setBarNote: (sectionId: string, barId: string, note: string | null) => void;
  setSectionKeyOverride: (sectionId: string, key: Key | null) => void;
  moveSectionUp: (sectionId: string) => void;
  moveSectionDown: (sectionId: string) => void;
  setSectionBarsPerRow: (sectionId: string, n: number | null) => void;

  // ── Bar operations ─────────────────────────────────────────
  addBar: (sectionId: string) => void;
  deleteBar: (sectionId: string, barId: string) => void;
  setRepeat: (
    sectionId: string,
    startBarId: string,
    endBarId: string,
    count: number,
  ) => void;
  removeRepeat: (sectionId: string, barId: string) => void;
  setBarInput: (
    sectionId: string,
    barId: string,
    input: string,
  ) => { ok: boolean; errors?: Array<{ token: string; error: string }> };
  setBarTimeSignature: (
    sectionId: string,
    barId: string,
    ts: TimeSignature | null,
  ) => void;
  toggleEighthNotes: (sectionId: string, barId: string) => void;
  setBarLyrics: (sectionId: string, barId: string, lyrics: string | null) => void;
  setBarRhythm: (sectionId: string, barId: string, positions: number[]) => void;
  duplicateBar: (sectionId: string, barId: string) => void;
  moveBarLeft: (sectionId: string, barId: string) => void;
  moveBarRight: (sectionId: string, barId: string) => void;

  // ── Transposition ──────────────────────────────────────────
  transposeSong: (semitones: number) => void;
  transposeBySemitones: (semitones: number) => void;  // alias for transposeSong

  // ── Undo / redo ────────────────────────────────────────────
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // ── Persistence ────────────────────────────────────────────
  loadSong: (song: Song) => void;
  loadFromLocalStorage: () => void;
  saveToLocalStorage: () => void;
  exportToFile: () => void;
  exportToJSON: () => string;
  importFromFile: (file: File) => Promise<void>;
  importFromJSON: (json: string) => { ok: boolean; error?: string };
  newSong: () => void;
  markClean: () => void;

  // ── Annotation operations ──────────────────────────────────
  addAnnotation: (xRatio: number, yOffset: number) => string;
  updateAnnotationPosition: (id: string, xRatio: number, yOffset: number) => void;
  updateAnnotationText: (id: string, text: string) => void;
  updateAnnotationStyle: (id: string, style: Partial<AnnotationStyle>) => void;
  deleteAnnotation: (id: string) => void;
}

// ------------------------------------------------------------
// STORE IMPLEMENTATION
// ------------------------------------------------------------

export const useSongStore = create<SongStore>((set, get) => {
  // ── Load persisted song on startup ──────────────────────────
  const persisted = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const file = JSON.parse(raw) as HChartFile;
      // Ensure annotations field exists on older saves
      if (!file.song.annotations) file.song.annotations = [];
      return file.song;
    } catch {
      return null;
    }
  })();

  const initialSong = persisted ?? makeDefaultSong();

  return {
    song: initialSong,
    selection: null,
    isDirty: false,
    undoStack: [],
    redoStack: [],

    // ── Selection ────────────────────────────────────────────
    setSelection: (selection) => set({ selection }),

    // ── Song metadata ─────────────────────────────────────────
    setSongTitle: (title) => {
      const { song, undoStack } = get();
      set({
        song: { ...song, title, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setSongKey: (key) => {
      const { song, undoStack } = get();
      const transposed = transposeSong(song, key);
      set({
        song: { ...transposed, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setSongTimeSignature: (ts) => {
      const { song, undoStack } = get();
      set({
        song: { ...song, timeSignature: ts, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setSongTempo: (tempo) => {
      const { song, undoStack } = get();
      set({
        song: { ...song, tempo, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setSongFeel: (feel) => {
      const { song, undoStack } = get();
      set({
        song: { ...song, feel, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setSongNote: (note) => {
      const { song, undoStack } = get();
      set({
        song: { ...song, note, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setDisplayMode: (mode) => {
      const { song } = get();
      set({ song: { ...song, displayMode: mode, updatedAt: new Date().toISOString() } });
    },

    setAnalysisSettings: (settings) => {
      const { song } = get();
      set({
        song: {
          ...song,
          analysisSettings: { ...song.analysisSettings, ...settings },
          updatedAt: new Date().toISOString(),
        },
      });
    },

    setSongBarsPerRow: (n) => {
      const { song, undoStack } = get();
      set({
        song: { ...song, barsPerRow: n, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    // ── Section operations ────────────────────────────────────
    addSection: (title = 'Section') => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: [...song.sections, makeDefaultSection(title)],
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    deleteSection: (sectionId) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.filter((s) => s.id !== sectionId),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    duplicateSection: (sectionId) => {
      const { song, undoStack } = get();
      const idx = song.sections.findIndex((s) => s.id === sectionId);
      if (idx === -1) return;
      const original = song.sections[idx];
      const clone: Section = {
        ...JSON.parse(JSON.stringify(original)),
        id: uuid(),
        title: original.title + ' (copy)',
      };
      const sections = [...song.sections];
      sections.splice(idx + 1, 0, clone);
      set({
        song: { ...song, sections, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    updateSectionTitle: (sectionId, title) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId ? { ...s, title } : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setSectionNote: (sectionId, note) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId ? { ...s, note } : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setBarNote: (sectionId, barId, note) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? {
                  ...s,
                  bars: s.bars.map((b) =>
                    b.id === barId ? { ...b, note } : b,
                  ),
                }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setSectionKeyOverride: (sectionId, key) => {
      const { song, undoStack } = get();
      const section = song.sections.find((s) => s.id === sectionId);
      if (!section) return;
      const currentKey = section.key ?? song.defaultKey;
      // setSectionKey(section, newKey, currentKey) — from transposer.ts
      const updatedSection = key !== null
        ? setSectionKey(section, key, currentKey)
        : { ...section, key: null };
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId ? updatedSection : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    moveSectionUp: (sectionId) => {
      const { song, undoStack } = get();
      const idx = song.sections.findIndex((s) => s.id === sectionId);
      if (idx <= 0) return;
      const sections = [...song.sections];
      [sections[idx - 1], sections[idx]] = [sections[idx], sections[idx - 1]];
      set({
        song: { ...song, sections, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    moveSectionDown: (sectionId) => {
      const { song, undoStack } = get();
      const idx = song.sections.findIndex((s) => s.id === sectionId);
      if (idx === -1 || idx >= song.sections.length - 1) return;
      const sections = [...song.sections];
      [sections[idx], sections[idx + 1]] = [sections[idx + 1], sections[idx]];
      set({
        song: { ...song, sections, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setSectionBarsPerRow: (sectionId, n) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId ? { ...s, barsPerRow: n } : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    // ── Bar operations ────────────────────────────────────────
    addBar: (sectionId) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? { ...s, bars: [...s.bars, makeDefaultBar()] }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    deleteBar: (sectionId, barId) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? { ...s, bars: s.bars.filter((b) => b.id !== barId) }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setRepeat: (sectionId, startBarId, endBarId, count) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? {
                  ...s,
                  bars: s.bars.map((b) => {
                    if (b.id === startBarId) return { ...b, repeatStart: true };
                    if (b.id === endBarId) return { ...b, repeatEnd: count };
                    return b;
                  }),
                }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    removeRepeat: (sectionId, barId) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? {
                  ...s,
                  bars: s.bars.map((b) =>
                    b.id === barId
                      ? { ...b, repeatStart: false, repeatEnd: null }
                      : b,
                  ),
                }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setBarInput: (sectionId, barId, input) => {
      const { song, undoStack } = get();
      const section = song.sections.find((s) => s.id === sectionId);
      if (!section) return { ok: false, errors: [] };
      const bar = section.bars.find((b) => b.id === barId);
      if (!bar) return { ok: false, errors: [] };

      const effectiveTs = bar.timeSignature ?? song.timeSignature;
      const effectiveK = section.key ?? song.defaultKey;
      const result = parseBar(input, effectiveTs, effectiveK);

      if (!result.ok) return { ok: false, errors: result.errors };

      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? {
                  ...s,
                  bars: s.bars.map((b) =>
                    b.id === barId ? { ...b, chords: result.chords } : b,
                  ),
                }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
      return { ok: true };
    },

    setBarTimeSignature: (sectionId, barId, ts) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? {
                  ...s,
                  bars: s.bars.map((b) =>
                    b.id === barId ? { ...b, timeSignature: ts } : b,
                  ),
                }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    toggleEighthNotes: (sectionId, barId) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? {
                  ...s,
                  bars: s.bars.map((b) =>
                    b.id === barId ? { ...b, eighthNotes: !b.eighthNotes } : b,
                  ),
                }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setBarLyrics: (sectionId, barId, lyrics) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? {
                  ...s,
                  bars: s.bars.map((b) =>
                    b.id === barId ? { ...b, lyrics } : b,
                  ),
                }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    setBarRhythm: (sectionId, barId, positions) => {
      const { song, undoStack } = get();
      const section = song.sections.find((s) => s.id === sectionId);
      if (!section) return;
      const bar = section.bars.find((b) => b.id === barId);
      if (!bar) return;

      const existingChords = bar.chords;
      const fallback = existingChords[existingChords.length - 1] ?? null;

      const updatedChords = positions.map((pos, i) => {
        const chord = existingChords[i] ?? fallback;
        if (chord) return { ...chord, position: pos };
        return {
          degree: 1 as Degree,
          accidental: null,
          quality: 'major' as ChordQuality,
          extensions: [] as ChordExtension[],
          slash: null,
          position: pos,
          borrowed: false,
          isEmpty: true,
        };
      });

      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId
              ? {
                  ...s,
                  bars: s.bars.map((b) =>
                    b.id === barId
                      ? { ...b, chords: updatedChords, hasCustomRhythm: true }
                      : b,
                  ),
                }
              : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    duplicateBar: (sectionId, barId) => {
      const { song, undoStack } = get();
      const section = song.sections.find((s) => s.id === sectionId);
      if (!section) return;
      const idx = section.bars.findIndex((b) => b.id === barId);
      if (idx === -1) return;
      const clone: Bar = { ...JSON.parse(JSON.stringify(section.bars[idx])), id: uuid() };
      const bars = [...section.bars];
      bars.splice(idx + 1, 0, clone);
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId ? { ...s, bars } : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    moveBarLeft: (sectionId, barId) => {
      const { song, undoStack } = get();
      const section = song.sections.find((s) => s.id === sectionId);
      if (!section) return;
      const idx = section.bars.findIndex((b) => b.id === barId);
      if (idx <= 0) return;
      const bars = [...section.bars];
      [bars[idx - 1], bars[idx]] = [bars[idx], bars[idx - 1]];
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId ? { ...s, bars } : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    moveBarRight: (sectionId, barId) => {
      const { song, undoStack } = get();
      const section = song.sections.find((s) => s.id === sectionId);
      if (!section) return;
      const idx = section.bars.findIndex((b) => b.id === barId);
      if (idx === -1 || idx >= section.bars.length - 1) return;
      const bars = [...section.bars];
      [bars[idx], bars[idx + 1]] = [bars[idx + 1], bars[idx]];
      set({
        song: {
          ...song,
          sections: song.sections.map((s) =>
            s.id === sectionId ? { ...s, bars } : s,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    // ── Transposition ─────────────────────────────────────────
    transposeSong: (semitones) => {
      const { song, undoStack } = get();
      const newKey = transposeKey(song.defaultKey, semitones);
      const transposed = transposeSong(song, newKey);
      set({
        song: { ...transposed, updatedAt: new Date().toISOString() },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    // ── Undo / redo ───────────────────────────────────────────
    undo: () => {
      const { undoStack, song, redoStack } = get();
      if (undoStack.length === 0) return;
      const prev = undoStack[undoStack.length - 1];
      set({
        song: prev,
        undoStack: undoStack.slice(0, -1),
        redoStack: [song, ...redoStack].slice(0, MAX_UNDO_STEPS),
        isDirty: true,
      });
    },

    redo: () => {
      const { redoStack, song, undoStack } = get();
      if (redoStack.length === 0) return;
      const next = redoStack[0];
      set({
        song: next,
        redoStack: redoStack.slice(1),
        undoStack: [...undoStack, song].slice(-MAX_UNDO_STEPS),
        isDirty: true,
      });
    },

    canUndo: () => get().undoStack.length > 0,
    canRedo: () => get().redoStack.length > 0,

    // ── Persistence ───────────────────────────────────────────
    loadSong: (song) => {
      // Migrate older saved songs — ensure all fields have concrete defaults
      const safeSong: Song = {
        ...song,
        annotations: song.annotations ?? [],
        note: song.note ?? null,
        sections: song.sections.map((s) => ({
          ...s,
          note: s.note ?? null,
          barsPerRow: s.barsPerRow ?? null,
          bars: s.bars.map((b) => ({
            ...b,
            note: b.note ?? null,
            lyrics: b.lyrics ?? null,
            hasCustomRhythm: b.hasCustomRhythm ?? false,
            repeatStart: b.repeatStart ?? false,
            repeatEnd: b.repeatEnd ?? null,
            eighthNotes: b.eighthNotes ?? false,
          })),
        })),
      };
      set({
        song: safeSong,
        selection: null,
        isDirty: false,
        undoStack: [],
        redoStack: [],
      });
    },

    exportToFile: () => {
      const { song } = get();
      const file: HChartFile = { version: FILE_VERSION, song };
      const blob = new Blob([JSON.stringify(file, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${song.title.replace(/\s+/g, '_')}.hchart`;
      a.click();
      URL.revokeObjectURL(url);
    },

    importFromFile: async (file) => {
      const text = await file.text();
      const parsed = JSON.parse(text) as HChartFile;
      get().loadSong(parsed.song);
    },

    markClean: () => set({ isDirty: false }),

    // ── Aliases matching the real store's API ─────────────────
    transposeBySemitones: (semitones) => get().transposeSong(semitones),

    loadFromLocalStorage: () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const file = JSON.parse(raw) as HChartFile;
        get().loadSong(file.song);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    },

    saveToLocalStorage: () => {
      try {
        const { song } = get();
        const file: HChartFile = { version: FILE_VERSION, song };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(file));
      } catch {
        // quota exceeded — ignore
      }
    },

    exportToJSON: () => {
      const { song } = get();
      const file: HChartFile = { version: FILE_VERSION, song };
      return JSON.stringify(file, null, 2);
    },

    importFromJSON: (json: string) => {
      try {
        const parsed = JSON.parse(json) as HChartFile;
        if (!parsed.song) return { ok: false, error: 'Invalid file format' };
        get().loadSong(parsed.song);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: String(e) };
      }
    },

    newSong: () => {
      const { undoStack, song } = get();
      set({
        song: makeDefaultSong(),
        selection: null,
        isDirty: false,
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
      });
    },
    addAnnotation: (xRatio, yOffset) => {
      const { song, undoStack } = get();
      const newAnnotation: Annotation = {
        id: uuid(),
        text: 'Annotation',
        xRatio: Math.max(0, Math.min(1, xRatio)),
        yOffset,
        style: { ...DEFAULT_ANNOTATION_STYLE },
      };
      set({
        song: {
          ...song,
          annotations: [...(song.annotations ?? []), newAnnotation],
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
      return newAnnotation.id;
    },

    updateAnnotationPosition: (id, xRatio, yOffset) => {
      const { song } = get();
      // No undo push on drag — too noisy. Commit on drag end if desired.
      set({
        song: {
          ...song,
          annotations: (song.annotations ?? []).map((a) =>
            a.id === id
              ? { ...a, xRatio: Math.max(0, Math.min(1, xRatio)), yOffset }
              : a,
          ),
          updatedAt: new Date().toISOString(),
        },
        isDirty: true,
      });
    },

    updateAnnotationText: (id, text) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          annotations: (song.annotations ?? []).map((a) =>
            a.id === id ? { ...a, text } : a,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    updateAnnotationStyle: (id, style) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          annotations: (song.annotations ?? []).map((a) =>
            a.id === id ? { ...a, style: { ...a.style, ...style } } : a,
          ),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },

    deleteAnnotation: (id) => {
      const { song, undoStack } = get();
      set({
        song: {
          ...song,
          annotations: (song.annotations ?? []).filter((a) => a.id !== id),
          updatedAt: new Date().toISOString(),
        },
        undoStack: pushUndo(undoStack, song),
        redoStack: [],
        isDirty: true,
      });
    },
  };
});

// ------------------------------------------------------------
// AUTOSAVE SUBSCRIBER
// Persists the song to localStorage on every change.
// ------------------------------------------------------------

useSongStore.subscribe((state) => {
  try {
    const file: HChartFile = { version: FILE_VERSION, song: state.song };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(file));
  } catch {
    // Storage quota exceeded or unavailable — silently ignore.
  }
});